import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { fleet, type CommandBatch, type FleetCommandRow } from "@/api/client";
import { ActionRow } from "@/components/ActionRow";
import { Chip, type ChipTone } from "@/components/Chip";
import { DataTable, type DataCol } from "@/components/DataTable";
import { Donut } from "@/components/Donut";
import { Icon } from "@/components/icons";
import { POLL, ago } from "@/lib/format";
import { LEVEL_LABEL, STATE_COLOR, STATE_MEANS, StateChip,
         commandLabel } from "@/modules/fleet/catalogue";

/** One run, as FOTA's `BatchCard` renders a rollout.
 *
 *  Same anatomy, deliberately: name and size on the header line, a status
 *  chip, the lifecycle buttons, a donut of the per-state counts, the same
 *  counts again as chips, and a Devices toggle that opens the per-device
 *  table. An operator who has watched a firmware rollout already knows how to
 *  read this.
 *
 *  POLL ONLY WHILE IT IS MOVING. FOTA refetches an `active` rollout at
 *  POLL.STATE and nothing otherwise, because a finished run's counts cannot
 *  change — and a page of finished runs each polling would be the cadence
 *  budget spent on nothing.
 */

const STATE_ORDER = [
  "pending", "sent", "received", "applied", "nacked",
  "superseded", "expired_undelivered", "expired_unapplied", "cancelled",
];

const BATCH_CHIP: Record<string, ChipTone> = {
  draft: "mute", active: "info", done: "ok",
  cancelled: "bad", expired: "warn",
};

export function RunCard({ b }: { b: CommandBatch }) {
  const [open, setOpen] = useState(false);
  const qc = useQueryClient();
  const detail = useQuery({
    queryKey: ["fleet", "batch", b.batch_id],
    queryFn: () => fleet.batch(b.batch_id),
    enabled: open || b.status === "active",
    refetchInterval: b.status === "active" ? POLL.STATE : false,
  });
  const counts = detail.data?.counts ?? {};

  const after = () => qc.invalidateQueries({ queryKey: ["fleet"] });
  const start = useMutation({ mutationFn: () => fleet.startBatch(b.batch_id),
                              onSuccess: after });
  const cancel = useMutation({ mutationFn: () => fleet.cancelBatch(b.batch_id),
                               onSuccess: after });
  const archive = useMutation({ mutationFn: () => fleet.archiveBatch(b.batch_id),
                                onSuccess: after });
  const err = start.error ?? cancel.error ?? archive.error;

  return (
    <div className="panel panel-pad" style={{ marginBottom: 12 }}>
      {/* TITLE, THEN `ActionRow` — not a hand-laid flex row with
          `marginLeft: auto` on the buttons. `tests/ui` refuses that, and the
          reason is in its own message: a CONVENTION is what produced 18 rows
          right and 18 left. FOTA's card predates the guard; new code does
          not get the exemption. */}
      <div style={{ marginBottom: 8 }}>
        <b style={{ color: "var(--t-bright)", fontSize: 13 }}>
          {b.name || commandLabel(b.action)}
        </b>
        <span style={{ fontSize: 11.5, color: "var(--t-mute)", marginLeft: 8 }}>
          {commandLabel(b.action)} · {b.commanded} asset
          {b.commanded === 1 ? "" : "s"}
          {b.level != null && ` · ${LEVEL_LABEL[b.level] ?? `level ${b.level}`}`}
          {b.wave_size ? ` · wave ${b.wave_size}` : ""}
        </span>
        <ActionRow top={8}>
          <button className="btn ghost" onClick={() => setOpen((s) => !s)}>
            {open ? "▾" : "▸"} Assets
          </button>
          <Chip tone={BATCH_CHIP[b.status] ?? "mute"}>
            {b.status.toUpperCase()}
          </Chip>
          {b.status === "draft" && (
            <button className="btn teal" disabled={start.isPending}
              onClick={() => start.mutate()}>
              <Icon name="play" /> Start
            </button>
          )}
          {/* CANCEL, not Stop: final, no resume (D-16) — the same word FOTA
              chose for the same reason. */}
          {b.status === "active" && (
            <button className="btn red" disabled={cancel.isPending}
              onClick={() => cancel.mutate()}>
              <Icon name="stop" /> Cancel
            </button>
          )}
          {/* RED, like FOTA's. Archiving takes a run out of the active list —
              something stops being available, which is exactly what red is
              reserved for in this console. */}
          {["done", "cancelled", "expired"].includes(b.status) && !b.archived_at && (
            <button className="btn red" disabled={archive.isPending}
              title="move out of the active list" onClick={() => archive.mutate()}>
              <Icon name="archive" /> Archive
            </button>
          )}
        </ActionRow>
      </div>

      {err && (
        <div style={{ fontSize: 11.5, color: "var(--bad)", marginBottom: 8 }}>
          {String((err as Error)?.message ?? "that did not work")}
        </div>
      )}

      {/* the chart: slices ARE the command states, coloured as their chips */}
      <Donut slices={STATE_ORDER
        .filter((st) => (counts[st] ?? 0) > 0)
        .map((st) => ({ label: st, value: counts[st] ?? 0,
                        color: STATE_COLOR[st] ?? "var(--t-mute)" }))} />

      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 10 }}>
        {STATE_ORDER.filter((st) => (counts[st] ?? 0) > 0).map((st) => (
          <span key={st} style={{ display: "inline-flex", alignItems: "center", gap: 5 }}
            title={STATE_MEANS[st] ?? st}>
            <StateChip state={st} />
            <b style={{ fontSize: 11.5, color: "var(--t-bright)" }}>{counts[st]}</b>
          </span>
        ))}
        {Object.values(counts).every((n) => !n) && (
          <span style={{ fontSize: 12, color: "var(--t-mute)" }}>
            {b.status === "draft"
              ? "Nothing sent — this run is a draft."
              : "No device state yet."}
          </span>
        )}
      </div>

      {open && detail.data && (
        <div style={{ marginTop: 10 }}>
          <DataTable rows={detail.data.devices} cols={RUN_COLS}
            rowKey={(d) => d.cmd_id} maxHeight={420}
            empty={<div className="panel panel-pad cap">No assets in this run.</div>} />
        </div>
      )}
    </div>
  );
}

/** Module-const so DataTable's memoized rows re-render only the assets whose
 *  state actually moved — the same reason FOTA keeps its columns out here. */
const RUN_COLS: DataCol<FleetCommandRow & { asset_id: string | null }>[] = [
  { key: "asset", label: "Asset", width: 180, mono: true,
    title: (d) => d.asset_id ?? d.connection_id,
    render: (d) => d.asset_id ?? d.connection_id },
  { key: "state", label: "State", width: 210,
    title: (d) => STATE_MEANS[d.state] ?? d.state,
    render: (d) => (
      <>
        <StateChip state={d.state} />
        <div style={{ fontSize: 11, color: "var(--t-mute)", marginTop: 2,
                      whiteSpace: "normal", lineHeight: 1.25 }}>
          {STATE_MEANS[d.state] ?? "—"}
        </div>
      </>
    ) },
  { key: "att", label: "Attempts", width: 80, render: (d) => d.attempts ?? 0 },
  { key: "when", label: "Created", width: 90,
    render: (d) => (d.created_at ? `${ago(d.created_at)} ago` : "—") },
];
